import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CHAT_MAX, CHAT_MIN, DEFAULT_CHAT_WIDTH, setChatWidth } from '../../state/uiSlice';

/** The narrowest the page beside the panel is still worth reading. */
const PAGE_FLOOR = 460;

/** What the panel may be dragged to at this viewport, whichever bound bites first. */
const ceiling = () => Math.max(CHAT_MIN, Math.min(CHAT_MAX, window.innerWidth - PAGE_FLOOR));

/**
 * The panel's left edge, as a handle.
 *
 * Dragging moves the CSS variable directly rather than dispatching per frame:
 * the shell, the sidebar and the whole screen live under that variable, and
 * re-rendering all of it sixty times a second to move one edge is the thing
 * that makes a resize feel heavy. Redux hears about it once, on release, which
 * is also when it is written to storage.
 *
 * The pointer is captured for the duration, so a fast drag that outruns the
 * cursor keeps resizing instead of dropping the handle over the page.
 */
export default function ChatResizer() {
  const dispatch = useDispatch();
  const chatWidth = useSelector((s) => s.ui.chatWidth);
  const grip = useRef(null);
  /* Read back on release; a ref, because the drag must not re-render. */
  const live = useRef(chatWidth);
  /* The running drag's teardown, or null when nothing is being dragged. */
  const end = useRef(null);

  /* A panel unmounted mid-drag would otherwise leave its listeners behind. */
  useEffect(() => () => end.current?.(), []);

  /* A window narrow enough to leave no page beside the panel pulls it in. The
     phone breakpoint makes the panel a full-screen sheet, so this is only ever
     about a desktop window being dragged smaller. */
  useEffect(() => {
    const onResize = () => {
      const max = ceiling();
      if (chatWidth > max) dispatch(setChatWidth(max));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [chatWidth, dispatch]);

  const onPointerDown = (e) => {
    /* A drag that somehow survived its own release ends here rather than
       running alongside a second one. */
    end.current?.();

    const shell = grip.current?.closest('.app');
    const panel = grip.current?.closest('.chatpane');
    if (!shell || !panel) return;

    /* Read now, not in the listeners below: React clears `currentTarget` once
       the handler returns, and every one of them runs after that. */
    const handle = e.currentTarget;
    const { pointerId } = e;

    /* Otherwise the browser starts a text selection across the page. */
    e.preventDefault();
    /* A nicety, not a requirement: it keeps a fast drag attached to the
       handle. The listeners below are on the window, so losing it costs
       nothing — and asking for it can throw if the pointer is already gone. */
    try { handle.setPointerCapture(pointerId); } catch { /* drag on regardless */ }
    /* Suspends the open/close transition: it would smear every frame of the
       drag over a quarter second and the edge would lag the cursor. */
    shell.classList.add('resizing');

    const edge = panel.getBoundingClientRect().right;
    const max = ceiling();
    /* Seeded so a press with no movement releases on the width it started at. */
    live.current = chatWidth;

    /**
     * Every way a drag can end, and it must survive all of them.
     *
     * A release the browser never reports — over another window, over the
     * browser's own chrome — used to leave the move listener attached and the
     * shell in its dragging state, so the edge went on following the bare
     * cursor. Idempotent, and cleared before anything that can throw, so one
     * failure cannot strand the rest.
     */
    const finish = () => {
      if (end.current !== finish) return;
      end.current = null;
      shell.classList.remove('resizing');
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      window.removeEventListener('blur', finish);
      handle.removeEventListener('lostpointercapture', finish);
      /* Releasing a pointer the browser has already let go of throws. */
      try {
        if (handle.hasPointerCapture?.(pointerId)) handle.releasePointerCapture(pointerId);
      } catch { /* already released */ }
      dispatch(setChatWidth(live.current));
    };

    function move(ev) {
      /* No button held means the release happened somewhere we were not told
         about. The next movement is the first chance to notice. */
      if (ev.buttons === 0) {
        finish();
        return;
      }
      live.current = Math.min(max, Math.max(CHAT_MIN, Math.round(edge - ev.clientX)));
      shell.style.setProperty('--chat-w', `${live.current}px`);
    }

    end.current = finish;
    /* On the window, not the handle: a drag that outruns the cursor leaves the
       8px strip, and the release has to be heard wherever it happens. */
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    window.addEventListener('blur', finish);
    handle.addEventListener('lostpointercapture', finish);
  };

  /* Keyboard: the same edge, in steps, for anyone not dragging it. */
  const onKeyDown = (e) => {
    const step = e.shiftKey ? 64 : 16;
    if (e.key === 'ArrowLeft') dispatch(setChatWidth(Math.min(ceiling(), chatWidth + step)));
    else if (e.key === 'ArrowRight') dispatch(setChatWidth(chatWidth - step));
    else return;
    e.preventDefault();
  };

  return (
    <div
      ref={grip}
      className="chatpane-grip"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the assistant panel"
      aria-valuenow={chatWidth}
      aria-valuemin={CHAT_MIN}
      aria-valuemax={CHAT_MAX}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => dispatch(setChatWidth(DEFAULT_CHAT_WIDTH))}
      title="Drag to resize · double-click to reset"
    />
  );
}
